import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { enumLabel, useI18n, type Locale } from '../lib/i18n';
import { formatDate, formatDateTime, formatNumber } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  statusTone,
  useToast,
} from '../components/ui';
import type { LeaveBalance } from './Leaves';

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

interface LeaveRequestDetail {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  reviewedAt: string | null;
  reviewNote: string | null;
  cancelledAt: string | null;
  createdAt: string;
  user: Person;
  department: { id: string; name: string; nameAr: string | null } | null;
  leaveType: { id: string; name: string; nameAr: string | null; isPaid: boolean };
  reviewedBy: Person | null;
  /** Its type's balance, for the year it starts in. */
  balance: LeaveBalance | null;
  can: { decide: boolean; cancel: boolean };
}

/** "31 Oct 2026" or "31 Oct 2026 – 2 Nov 2026". */
function leaveDates(request: { startDate: string; endDate: string }, locale: Locale): string {
  const start = formatDate(request.startDate, locale);
  return request.endDate === request.startDate ? start : `${start} – ${formatDate(request.endDate, locale)}`;
}

/**
 * Route: `/leaves/:id` — one leave request: whose, which, when, why, what was
 * decided, and the year's balance of its type; and deciding or cancelling it,
 * for whoever may. A notification about a request opens here (operator,
 * 2026-10-11: "i must go to details for this notification not main page").
 */
export function LeaveDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: request, isLoading, error, refetch } = useQuery({
    queryKey: ['leaves', 'one', id],
    queryFn: () => api.get<LeaveRequestDetail>(`/leaves/${id}`),
  });

  const settled = (message: string) => {
    toast.success(message);
    void queryClient.invalidateQueries({ queryKey: ['leaves'] });
  };
  const approve = useMutation({
    mutationFn: () => api.post(`/leaves/${id}/approve`, {}),
    onSuccess: () => settled(t('leaves.approved')),
    onError: (e: Error) => toast.error(e.message),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/leaves/${id}/cancel`),
    onSuccess: () => settled(t('leaves.cancelled')),
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !request) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const own = request.userId === user?.id;
  // Up to my requests, to the queue it waits in, or to everyone's.
  const back = own ? '/leaves' : request.can.decide ? '/leaves?tab=review' : '/leaves?tab=all';
  const busy = approve.isPending || cancel.isPending;

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('leaves.title')}
        </Link>
      </div>

      <PageHeader
        title={localized(request.leaveType)}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={statusTone(request.status)}>{enumLabel(request.status, locale)}</Badge>
            {!own && <span>{localized(request.user)}</span>}
            <span className="tabular">{leaveDates(request, locale)}</span>
          </span>
        }
        action={
          (request.can.decide || request.can.cancel) && (
            <>
              {request.can.decide && (
                <>
                  <Button
                    variant="primary"
                    icon={<Check className="h-4 w-4" />}
                    loading={approve.isPending}
                    disabled={busy}
                    onClick={() => approve.mutate()}
                  >
                    {t('common.approve')}
                  </Button>
                  <Button
                    variant="danger"
                    icon={<X className="h-4 w-4" />}
                    disabled={busy}
                    onClick={() => navigate(`/leaves/${request.id}/reject`)}
                  >
                    {t('common.reject')}
                  </Button>
                </>
              )}
              {request.can.cancel && (
                <Button loading={cancel.isPending} disabled={busy} onClick={() => cancel.mutate()}>
                  {t('leaves.cancelRequest')}
                </Button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          {request.reviewNote && (
            <Card className={request.status === 'REJECTED' ? 'border-error/40' : 'border-primary/30'}>
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {request.reviewedBy ? t('leaves.noteFrom', { name: localized(request.reviewedBy) }) : t('leaves.note')}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {request.reviewNote}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('leaves.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.employee')}>
                <span className="inline-flex flex-col items-end">
                  <span>{localized(request.user)}</span>
                  {request.department && <span className="text-caption text-muted">{localized(request.department)}</span>}
                </span>
              </Detail>
              <Detail label={t('leaves.type')}>
                {localized(request.leaveType)} · {request.leaveType.isPaid ? t('leaves.paid') : t('leaves.unpaid')}
              </Detail>
              <Detail label={t('leaves.dates')}>
                <span className="tabular">{leaveDates(request, locale)}</span>
              </Detail>
              <Detail label={t('leaves.days')}>
                <span className="tabular">{formatNumber(request.days, locale)}</span>
              </Detail>
              <Detail label={t('leaves.asked')}>
                <span className="tabular">{formatDateTime(request.createdAt, locale)}</span>
              </Detail>
              {request.reviewedBy && (
                <Detail label={t('leaves.reviewedBy')}>
                  <span className="inline-flex flex-col items-end">
                    <span>{localized(request.reviewedBy)}</span>
                    {request.reviewedAt && (
                      <span className="text-caption text-muted tabular">{formatDateTime(request.reviewedAt, locale)}</span>
                    )}
                  </span>
                </Detail>
              )}
              {request.cancelledAt && (
                <Detail label={t('leaves.cancelledOn')}>
                  <span className="tabular">{formatDateTime(request.cancelledAt, locale)}</span>
                </Detail>
              )}
            </dl>
            {request.reason && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.reason')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {request.reason}
                </p>
              </div>
            )}
          </Card>
        </div>

        {request.balance && (
          <div className="min-w-0">
            <BalanceCard balance={request.balance} />
          </div>
        )}
      </div>
    </>
  );
}

/** The year's balance of the request's type: what it allows, what is taken and waiting, what is left. */
function BalanceCard({ balance }: { balance: LeaveBalance }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card>
      <CardHeader title={t('leaves.balance', { year: String(balance.year) })} subtitle={localized(balance.leaveType)} />
      <dl className="divide-y divide-[var(--line)]">
        <Detail label={t('leaves.allowance')}>
          {balance.allowance == null ? t('leaves.noAllowance') : <span className="tabular">{formatNumber(balance.allowance, locale)}</span>}
          {!!balance.grantedDays && (
            <span className="ms-1.5 text-caption text-muted">({t('leaves.grantedDays', { n: formatNumber(balance.grantedDays, locale) })})</span>
          )}
        </Detail>
        <Detail label={t('leaves.used')}>
          <span className="tabular">{formatNumber(balance.approvedDays, locale)}</span>
          {!!balance.deductedDays && (
            <span className="ms-1.5 text-caption text-muted">({t('leaves.deductedDays', { n: formatNumber(balance.deductedDays, locale) })})</span>
          )}
        </Detail>
        <Detail label={t('leaves.pending')}>
          <span className="tabular">{formatNumber(balance.pendingDays, locale)}</span>
        </Detail>
        {balance.remaining != null && (
          <Detail label={t('leaves.remaining')}>
            <span className={balance.remaining < 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular'}>
              {formatNumber(balance.remaining, locale)}
            </span>
          </Detail>
        )}
      </dl>
    </Card>
  );
}
